import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAutoScroll } from '../hooks/useAutoScroll';
import { getApiUrl } from '../config/api';
import { validateKenyanInsuredPhone } from '../utils/kenyanPhoneValidation';
import {
  VALIDATION_METHOD,
  ID_VALIDATE_CLIENT_TIMEOUT_MS,
  formatKraErrorMessage,
  resolveFailedValidationMethod,
  allowsManualNameAndPin,
  isNameFieldDisabled,
  isKraPinFieldDisabled,
  isInsuredFormComplete
} from '../utils/insuredDetailsValidation';

const INSTITUTIONAL_CLASS = 'Motor Commercial Institutional';
const MIN_BRN_LEN = 3;
const MAX_BRN_LEN = 50;

const InsuredDetails = ({ coverType, selectedClass, startDate, coverPeriod, endDate, initialData, onComplete, onBack }) => {
  const isInstitutional = selectedClass === INSTITUTIONAL_CLASS;

  const [insuredData, setInsuredData] = useState({
    idNumber: initialData?.idNumber || '',
    insuredName: initialData?.insuredName || '',
    emailAddress: initialData?.emailAddress || '',
    phoneNumber: initialData?.phoneNumber || '',
    kraPin: initialData?.kraPin || ''
  });

  const [isLoadingKRA, setIsLoadingKRA] = useState(false);
  const [kraError, setKraError] = useState('');
  const [kraSuccess, setKraSuccess] = useState(false);
  const [validationMethod, setValidationMethod] = useState('');
  const [requiresManualKraPin, setRequiresManualKraPin] = useState(false);

  // Debounce timer for email validation (to allow slower typing)
  const [emailDebounceTimer, setEmailDebounceTimer] = useState(null);

  // Auto-scroll setup
  const fieldOrder = ['idNumber', 'insuredName', 'kraPin', 'emailAddress', 'phoneNumber'];
  
  const isFieldValid = (fieldName, value) => {
    if (!value || value.toString().trim() === '') return false;

    switch (fieldName) {
      case 'idNumber':
        if (isInstitutional) {
          const t = String(value).trim();
          return t.length >= MIN_BRN_LEN && t.length <= MAX_BRN_LEN;
        }
        // Accept IDs with 5-10 digits (some valid IDs may have fewer than 8 digits)
        return value.length >= 5 && value.length <= 10;
      case 'insuredName':
        return value.trim().length > 0;
      case 'kraPin':
        return value.length === 11;
      case 'emailAddress':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      case 'phoneNumber':
        const phoneStatus = getPhoneNumberStatus();
        return phoneStatus.status === 'valid';
      default:
        return false;
    }
  };

  const { 
    initializeFieldStates, 
    createInputChangeHandler, 
    handleFocus,
    handleBlur,
    scrollToTop
  } = useAutoScroll(fieldOrder, isFieldValid);

  // Initialize field states and scroll to top on component mount
  useEffect(() => {
    initializeFieldStates(fieldOrder);
    scrollToTop(); // Scroll to top when user enters this step
  }, [initializeFieldStates, scrollToTop]);



  const validateIDNumber = async (idNumber) => {
    if (!idNumber || idNumber.length < 5) return; // Don't validate if too short
    
    setIsLoadingKRA(true);
    setKraError('');
    setValidationMethod('');
    setRequiresManualKraPin(false);

    // Use API config to auto-detect the correct backend URL (works for both internal and external access)
    const apiUrl = getApiUrl('/api/validate-id');

    const applyIndividualValidationFailure = ({
      rawMsg,
      requiresManualInput = false,
      isTimeout = false,
      errorCode,
      axiosCode
    }) => {
      const method = resolveFailedValidationMethod({
        isInstitutional: false,
        errorMessage: rawMsg,
        requiresManualInput,
        isTimeout,
        errorCode,
        axiosCode
      });
      setKraError(
        formatKraErrorMessage(rawMsg, { isTimeout, errorCode, axiosCode })
      );
      setKraSuccess(false);
      setValidationMethod(method);
      setRequiresManualKraPin(allowsManualNameAndPin(method));
    };
    
    try {
      const response = await axios.post(apiUrl, {
        idNumber: idNumber
      }, {
        // Must exceed backend hard deadline so a timeout payload (not axios abort) is received.
        timeout: ID_VALIDATE_CLIENT_TIMEOUT_MS
      });
      
      if (response.data && response.data.success) {
        // Auto-fill the name from the response
        // Store masked name for display, but also store full name for bank/DB
        setInsuredData(prev => ({
          ...prev,
          insuredName: response.data.name || '', // Masked name for display
          insuredNameFull: response.data.fullName || response.data.name || '', // Full name for bank/DB
          kraPin: response.data.kraPin || ''
        }));
        
        setValidationMethod(response.data.validationMethod);
        setRequiresManualKraPin(response.data.requiresManualKraPin);
        setKraSuccess(true);
        setKraError('');
        
      } else {
        // Server returned 200 but success: false
        const rawMsg =
          response.data?.error ||
          response.data?.message ||
          'ID validation failed. Please check your ID number.';
        applyIndividualValidationFailure({
          rawMsg,
          requiresManualInput: !!response.data?.requiresManualInput,
          isTimeout: !!response.data?.isTimeout,
          errorCode: response.data?.errorCode
        });
      }
    } catch (error) {
      console.error('❌ InsuredDetails - Validation error:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        url: apiUrl
      });

      const payload = error.response?.data || {};
      const rawKraMessage = payload.error || payload.message;
      const requiresManualInput = !!payload.requiresManualInput;

      if (rawKraMessage) {
        applyIndividualValidationFailure({
          rawMsg: rawKraMessage,
          requiresManualInput,
          isTimeout: !!payload.isTimeout,
          errorCode: payload.errorCode,
          axiosCode: error.code
        });
      } else if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
        applyIndividualValidationFailure({
          rawMsg: error.message || 'Validation timed out',
          requiresManualInput: false,
          isTimeout: true,
          axiosCode: error.code
        });
      } else if (requiresManualInput) {
        applyIndividualValidationFailure({
          rawMsg: 'Validation services are currently unavailable. Please try again later or contact support.',
          requiresManualInput: true,
          axiosCode: error.code
        });
      } else if (error.code === 'ERR_NETWORK') {
        setKraError('Network error. Please check your connection and try again.');
        setKraSuccess(false);
      } else if (error.response?.status === 500) {
        setKraError('Validation service error. Please try again in a few moments.');
        setKraSuccess(false);
      } else if (error.response?.status === 400) {
        setKraError('Invalid ID number format. Please check and try again.');
        setKraSuccess(false);
      } else {
        setKraError('Validation service temporarily unavailable. Please try again.');
        setKraSuccess(false);
      }
    } finally {
      setIsLoadingKRA(false);
    }
  };

  const validateBusinessRegistration = async (brnRaw) => {
    const brn = String(brnRaw || '').trim();
    if (brn.length < MIN_BRN_LEN) return;

    setIsLoadingKRA(true);
    setKraError('');
    setValidationMethod('');
    setRequiresManualKraPin(false);

    const apiUrl = getApiUrl('/api/validate-business-registration');

    try {
      const response = await axios.post(
        apiUrl,
        { business_registration_number: brn },
        { timeout: 25000 }
      );

      if (response.data && response.data.success) {
        setInsuredData((prev) => ({
          ...prev,
          insuredName: response.data.name || '',
          insuredNameFull:
            response.data.fullName || response.data.name || '',
          kraPin: response.data.kraPin || ''
        }));

        setValidationMethod(response.data.validationMethod);
        setRequiresManualKraPin(response.data.requiresManualKraPin);
        setKraSuccess(true);
        setKraError('');
      } else {
        const rawMsg =
          response.data?.error ||
          response.data?.message ||
          'Business registration validation failed. Please check the number.';
        setKraError(formatKraErrorMessage(rawMsg));
        setKraSuccess(false);
        setValidationMethod(
          resolveFailedValidationMethod({
            isInstitutional: true,
            errorMessage: rawMsg,
            requiresManualInput: !!response.data?.requiresManualInput
          })
        );
        setRequiresManualKraPin(true);
      }
    } catch (error) {
      console.error('❌ InsuredDetails - Business registration validation error:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        url: apiUrl
      });

      const applyInstitutionalManualFailure = (message) => {
        setKraError(formatKraErrorMessage(message));
        setKraSuccess(false);
        setValidationMethod(VALIDATION_METHOD.INSTITUTIONAL_MANUAL);
        setRequiresManualKraPin(true);
      };

      const rawKraMessage = error.response?.data?.error || error.response?.data?.message;
      if (rawKraMessage) {
        applyInstitutionalManualFailure(rawKraMessage);
      } else if (error.response?.data?.requiresManualInput) {
        applyInstitutionalManualFailure(
          'Validation services are currently unavailable. Please try again later or contact support.'
        );
      } else if (error.code === 'ECONNABORTED') {
        applyInstitutionalManualFailure('Validation service timeout. Please try again.');
      } else if (error.code === 'ERR_NETWORK') {
        applyInstitutionalManualFailure(
          'Network error. Please check your connection and try again.'
        );
      } else if (error.response?.status === 500) {
        applyInstitutionalManualFailure(
          'Validation service error. Please try again in a few moments.'
        );
      } else if (error.response?.status === 400) {
        applyInstitutionalManualFailure(
          error.response?.data?.error ||
            'Invalid business registration number. Please check and try again.'
        );
      } else if (error.message?.includes('timeout')) {
        applyInstitutionalManualFailure('Validation service timeout. Please try again.');
      } else {
        applyInstitutionalManualFailure(
          'Validation service temporarily unavailable. Please try again.'
        );
      }
    } finally {
      setIsLoadingKRA(false);
    }
  };

  const runIdentifierValidation = () => {
    if (isInstitutional) {
      validateBusinessRegistration(insuredData.idNumber);
    } else {
      validateIDNumber(insuredData.idNumber);
    }
  };

  const handleInputChange = createInputChangeHandler(setInsuredData);

  // Enhanced input change handler with auto-validation
  const enhancedInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Special handling for KRA PIN
    if (name === 'kraPin') {
      processedValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 11);
    }

    // Special handling for phone number
    if (name === 'phoneNumber') {
      processedValue = value.replace(/[^0-9+]/g, '');
    }

    // ID number (individual) or business registration number (institutional)
    if (name === 'idNumber') {
      if (isInstitutional) {
        // Business reg: letters, digits, common punctuation (e.g. C.19574, BN/2024/123)
        processedValue = value
          .replace(/[^\w.\-\/ (),&:;#+]/gi, '')
          .slice(0, MAX_BRN_LEN);
      } else {
        processedValue = value.replace(/[^0-9]/g, '').slice(0, 10);
      }
    }

    setInsuredData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    if (name === 'idNumber') {
      if (kraSuccess || kraError) {
        setKraSuccess(false);
        setKraError('');
      }

      if (!isInstitutional && (processedValue.length === 8 || processedValue.length === 10)) {
        validateIDNumber(processedValue);
      }
    }

    // Debounce email validation to allow slower typing
    if (name === 'emailAddress') {
      // Clear existing timer
      if (emailDebounceTimer) {
        clearTimeout(emailDebounceTimer);
      }
      
      // Set new timer for email validation (1.5s delay)
      const newTimer = setTimeout(() => {
        handleInputChange(e);
      }, 1500);
      
      setEmailDebounceTimer(newTimer);
    } else {
      // Pass processed value so useAutoScroll does not overwrite (e.g. BRN must stay alphanumeric)
      handleInputChange({ target: { name, value: processedValue } });
    }
  };
  
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (emailDebounceTimer) {
        clearTimeout(emailDebounceTimer);
      }
    };
  }, [emailDebounceTimer]);

  const handleComplete = () => {
    if (isFormComplete()) {
      const payload = {
        ...insuredData,
        // Manual iPage / institutional entry: persist typed name as full name for core/DMVIC
        insuredNameFull:
          insuredData.insuredNameFull || insuredData.insuredName || ''
      };
      onComplete(payload);
    }
  };

  const isFormComplete = () => {
    // Exclude optional full-name mirror used for downstream systems
    const { insuredNameFull: _full, ...requiredFields } = insuredData;
    const hasAllValues = Object.values(requiredFields).every(
      (value) => String(value ?? '').trim() !== ''
    );
    const phoneOk = getPhoneNumberStatus().status === 'valid';

    return isInsuredFormComplete({
      validationMethod,
      hasAllValues,
      phoneOk,
      kraSuccess,
      insuredName: insuredData.insuredName,
      kraPin: insuredData.kraPin
    });
  };

  const getKRAPINStatus = () => {
    const kraPin = insuredData.kraPin;
    if (!kraPin) return { status: 'empty', message: 'Enter your KRA PIN', progress: 0 };
    if (kraPin.length < 11) return { 
      status: 'short', 
      message: `${11 - kraPin.length} characters remaining`, 
      progress: (kraPin.length / 11) * 100 
    };
    if (kraPin.length === 11) return { 
      status: 'valid', 
      message: 'Perfect! 11 characters', 
      progress: 100 
    };
    return { status: 'long', message: 'Too long (max 11)', progress: 100 };
  };

  const getPhoneNumberStatus = () => {
    const phoneNumber = insuredData.phoneNumber;
    if (!phoneNumber) return { status: 'empty', message: 'Enter phone number' };

    const strict = validateKenyanInsuredPhone(phoneNumber);
    if (strict.valid) {
      return { status: 'valid', message: 'Valid Kenyan mobile number' };
    }
    
    // Check if it starts with +254
    if (phoneNumber.startsWith('+254')) {
      const remainingDigits = phoneNumber.substring(4); // Remove +254
      if (remainingDigits.length === 0) {
        return { status: 'short', message: 'Enter 9 digits after +254' };
      }
      if (remainingDigits.length < 9) {
        return { status: 'short', message: `${9 - remainingDigits.length} digits remaining after +254` };
      }
      if (remainingDigits.length === 9) {
        return { status: 'invalid', message: strict.message || 'Enter a valid Kenyan mobile (07XXXXXXXX or 01XXXXXXXX)' };
      }
      if (remainingDigits.length > 9) {
        return { status: 'long', message: 'Too many digits after +254 (max 9)' };
      }
    }
    
    // Check if it starts with 0 (local format)
    if (phoneNumber.startsWith('0')) {
      const remainingDigits = phoneNumber.substring(1); // Remove 0
      if (remainingDigits.length < 9) {
        return { status: 'short', message: `${9 - remainingDigits.length} digits remaining after 0` };
      }
      if (remainingDigits.length === 9) {
        return { status: 'invalid', message: strict.message || 'Enter a valid Kenyan mobile (07XXXXXXXX or 01XXXXXXXX)' };
      }
      if (remainingDigits.length > 9) {
        return { status: 'long', message: 'Too many digits after 0 (max 9)' };
      }
    }
    
    // Check if it starts with 254 (without +)
    if (phoneNumber.startsWith('254')) {
      const remainingDigits = phoneNumber.substring(3); // Remove 254
      if (remainingDigits.length < 9) {
        return { status: 'short', message: `${9 - remainingDigits.length} digits remaining after 254` };
      }
      if (remainingDigits.length === 9) {
        return { status: 'invalid', message: strict.message || 'Enter a valid Kenyan mobile (07XXXXXXXX or 01XXXXXXXX)' };
      }
      if (remainingDigits.length > 9) {
        return { status: 'long', message: 'Too many digits after 254 (max 9)' };
      }
    }
    
    // Generic validation for other formats — must still pass Kenyan mobile rules
    if (phoneNumber.length < 10) {
      return { status: 'short', message: strict.message || 'Phone number too short' };
    }
    if (phoneNumber.length > 15) {
      return { status: 'long', message: 'Phone number too long' };
    }
    
    return { status: 'invalid', message: strict.message || 'Enter a valid Kenyan mobile (07XXXXXXXX or 01XXXXXXXX)' };
  };

  const kraPinStatus = getKRAPINStatus();
  const phoneStatus = getPhoneNumberStatus();

  const pageTitle = isInstitutional
    ? 'Insured entity details'
    : 'Insured Person Details';
  const pageSubtitle = isInstitutional
    ? `Please provide the institution or company information for ${coverType} cover — ${selectedClass}.`
    : `Please provide your personal information for ${coverType} cover - ${selectedClass}`;
  const sectionHeading = isInstitutional
    ? '🏢 Institutional / company information'
    : '👤 Personal Information';
  const idFieldLabel = isInstitutional
    ? 'Business Registration Number *'
    : 'ID Number *';
  const nameFieldLabel = isInstitutional
    ? 'Institutional / company name *'
    : 'Insured Name *';
  const idPlaceholder = isInstitutional
    ? 'e.g. C.19574 (enter then validate)'
    : 'Enter your ID number (5-10 digits)';
  const idMaxLen = isInstitutional ? MAX_BRN_LEN : 10;
  const showManualValidateId =
    !isInstitutional &&
    insuredData.idNumber.length >= 5 &&
    insuredData.idNumber.length !== 8 &&
    insuredData.idNumber.length !== 10 &&
    !kraSuccess;
  const showManualValidateBrn =
    isInstitutional &&
    insuredData.idNumber.trim().length >= MIN_BRN_LEN &&
    !kraSuccess;
  const showRevalidateId =
    !isInstitutional &&
    insuredData.idNumber.length >= 5 &&
    insuredData.idNumber.length <= 10 &&
    kraSuccess;
  const showRevalidateBrn =
    isInstitutional &&
    insuredData.idNumber.trim().length >= MIN_BRN_LEN &&
    kraSuccess;

  return (
    <div className="insured-details">
      <h1 className="main-title">{pageTitle}</h1>
      <p className="subtitle">{pageSubtitle}</p>
      

      
      <div className="form-section">
        <h3>{sectionHeading}</h3>
        

        
        <div className="form-group">
          <label htmlFor="idNumber">{idFieldLabel}</label>
          <div className="id-input-wrapper">
            <input
              type="text"
              id="idNumber"
              name="idNumber"
              value={insuredData.idNumber}
              onChange={enhancedInputChange}
              onFocus={() => handleFocus('idNumber')}
              onBlur={() => handleBlur('idNumber')}
              placeholder={idPlaceholder}
              required
              maxLength={idMaxLen}
              disabled={isLoadingKRA}
            />
            {showManualValidateId && (
              <button
                type="button"
                className="btn-validate-id"
                onClick={runIdentifierValidation}
                disabled={isLoadingKRA}
              >
                {isLoadingKRA ? '⏳ Validating...' : '✓ Validate ID'}
              </button>
            )}
            {showManualValidateBrn && (
              <button
                type="button"
                className="btn-validate-id"
                onClick={runIdentifierValidation}
                disabled={isLoadingKRA}
              >
                {isLoadingKRA ? '⏳ Validating...' : '✓ Validate registration'}
              </button>
            )}
          </div>
          {kraError && <div className="error-message">{kraError}</div>}
          {isLoadingKRA && (
            <div className="loading-message">
              <div className="loading-spinner"></div>
              Validating...
            </div>
          )}
          {kraSuccess && (
            <div className="success-message">
              {isInstitutional
                ? '✅ Registration validated successfully. Company details were retrieved.'
                : '✅ ID validated successfully! Your information has been retrieved.'}
            </div>
          )}
          
          {(validationMethod === VALIDATION_METHOD.MANUAL ||
            allowsManualNameAndPin(validationMethod)) &&
            kraError && (
            <div className="retry-section">
              <button 
                type="button" 
                className="btn-retry"
                onClick={runIdentifierValidation}
              >
                🔄 Retry Validation
              </button>
              {validationMethod === VALIDATION_METHOD.TIMEOUT_MANUAL && (
                <small>
                  You can enter the insured name and PIN below to continue.
                </small>
              )}
            </div>
          )}
          
          <small>
            {isInstitutional
              ? `Enter the business registration number (${MIN_BRN_LEN}–${MAX_BRN_LEN} characters), then click Validate registration.`
              : insuredData.idNumber.length >= 5 &&
                  insuredData.idNumber.length !== 8 &&
                  insuredData.idNumber.length !== 10
                ? "ID numbers can be 5-10 digits. Click 'Validate ID' to verify."
                : "Enter your ID number (5-10 digits). Auto-validates at 8 or 10 digits."}
          </small>
          {showRevalidateId && (
            <button 
              type="button" 
              className="btn-secondary btn-revalidate"
              onClick={runIdentifierValidation}
              disabled={isLoadingKRA}
            >
              🔄 Re-validate ID Number
            </button>
          )}
          {showRevalidateBrn && (
            <button
              type="button"
              className="btn-secondary btn-revalidate"
              onClick={runIdentifierValidation}
              disabled={isLoadingKRA}
            >
              🔄 Re-validate registration
            </button>
          )}
        </div>
        
        <div className="form-group">
          <label htmlFor="insuredName">{nameFieldLabel}</label>
          <input
            type="text"
            id="insuredName"
            name="insuredName"
            value={insuredData.insuredName}
            onChange={enhancedInputChange}
            onFocus={() => handleFocus('insuredName')}
            onBlur={() => handleBlur('insuredName')}
            placeholder={
              allowsManualNameAndPin(validationMethod)
                ? (isInstitutional ? 'Enter institutional / company name' : 'Enter insured name manually')
                : 'Will be auto-filled from validation'
            }
            required
            disabled={isNameFieldDisabled(validationMethod)}

          />
          

          
          <small>
            {validationMethod === VALIDATION_METHOD.MANUAL
              ? "Please try again later when services are available."
              : validationMethod === VALIDATION_METHOD.TIMEOUT_MANUAL
                ? "Validation timed out. Enter the insured name manually to continue, or retry."
                : validationMethod === VALIDATION_METHOD.IPAGE_MANUAL
                ? "iPage validation failed. Enter the Insured Name manually to continue."
                : validationMethod === VALIDATION_METHOD.INSTITUTIONAL_MANUAL
                  ? "Registration lookup failed. Enter the institutional / company name manually to continue."
                  : isInstitutional
                    ? 'Registered name is populated from the business registration lookup (display may be masked).'
                    : "Your name is automatically populated from our database"
            }
          </small>
        </div>
        
        <div className="form-group">
          <label htmlFor="kraPin">KRA PIN *</label>
          <input
            type="text"
            id="kraPin"
            name="kraPin"
            value={insuredData.kraPin}
            onChange={enhancedInputChange}
            onFocus={() => handleFocus('kraPin')}
            onBlur={() => handleBlur('kraPin')}
            placeholder={
              requiresManualKraPin || allowsManualNameAndPin(validationMethod)
                ? "Enter your KRA PIN manually"
                : "Will be auto-filled from validation"
            }
            required
            maxLength={11}
            disabled={isKraPinFieldDisabled(validationMethod)}

          />
          <div className="input-status">
            <span className={`status-indicator ${kraPinStatus.status}`}>
              {kraPinStatus.status === 'valid' ? '✅' : 
               kraPinStatus.status === 'empty' ? '⭕' : '⚠️'}
            </span>
            <span className={`status-message ${kraPinStatus.status}`}>
              {kraPinStatus.message}
            </span>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ width: `${kraPinStatus.progress}%` }}
              ></div>
            </div>
            <span className="progress-text">{Math.round(kraPinStatus.progress)}%</span>
          </div>
          

          
          <small>
            {validationMethod === VALIDATION_METHOD.MANUAL
              ? "Please try again later when services are available."
              : validationMethod === VALIDATION_METHOD.TIMEOUT_MANUAL
                ? "Enter your PIN manually to continue, or retry validation."
                : allowsManualNameAndPin(validationMethod) || requiresManualKraPin
                  ? "Please enter your KRA PIN"
                  : "Your KRA PIN is automatically populated from our database"
            }
          </small>
        </div>
        

        
        <div className="form-group">
          <label htmlFor="emailAddress">Email Address *</label>
          <input
            type="email"
            id="emailAddress"
            name="emailAddress"
            value={insuredData.emailAddress}
            onChange={enhancedInputChange}
            onFocus={() => handleFocus('emailAddress')}
            onBlur={() => handleBlur('emailAddress')}
            placeholder="Enter your email address"
            required
          />
          <small>We'll send your policy documents to this email</small>
        </div>
        
        <div className="form-group">
          <label htmlFor="phoneNumber">Insured Phone Number *</label>
          <input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            value={insuredData.phoneNumber}
            onChange={enhancedInputChange}
            onFocus={() => handleFocus('phoneNumber')}
            onBlur={() => handleBlur('phoneNumber')}
            placeholder="e.g., +254 700 000 000 or 0700 000 000"
            required
            maxLength={15}
          />
          <div className="input-status">
            <span className={`status-indicator ${phoneStatus.status}`}>
              {phoneStatus.status === 'valid' ? '✅' : 
               phoneStatus.status === 'empty' ? '⭕' : '⚠️'}
            </span>
            <span className={`status-message ${phoneStatus.status}`}>
              {phoneStatus.message}
            </span>
          </div>
          <small>
            <strong>Format:</strong> +254 followed by 9 digits (e.g., +254700000000) or 0 followed by 9 digits (e.g., 0700000000)
          </small>
          {/* WhatsApp delivery notification commented out - we are not delivering via WhatsApp
          <div className="whatsapp-notification">
            <div className="whatsapp-icon">📱</div>
            <div className="whatsapp-message">
              <strong>WhatsApp Certificate Delivery:</strong> Your insurance certificate will be sent to this WhatsApp number. Please ensure it's correct and active.
            </div>
          </div>
          */}
        </div>
        

      </div>
      
      <div className="navigation-buttons">
        <button className="btn-secondary" onClick={onBack}>
          ← Back to Cover Details
        </button>
        <button
          id="continueButton"
          className="btn-primary"
          onClick={handleComplete}
          disabled={!isFormComplete()}
        >
          Continue to Vehicle Details →
        </button>
      </div>
    </div>
  );
};

export default InsuredDetails;
